import { useBeeswaxAccountAudienceList } from "@/modules/Audience/AccountAudience/AccountAudience.hook";
import { useEmailAudience } from "@/modules/Audience/EmailAudience/emailAudience.hook";
import {
  useSiteVistorAudienceList,
  useTargetableVisitorInsightAudienceList,
} from "@/modules/Audience/SiteVisitorAudience/siteVisitorAudience.hook";
import {
  AUDIENCE_REF_TYPES,
  AUDIENCE_TYPES,
} from "@/modules/Campaign/campaign.constant";
import { useMemo } from "react";

// Every row carries the ref type it should be saved as, because a picker type does
// not map 1:1 to a model — "Site Visitor" lists retarget and Visitor Insight
// audiences side by side.
function withRefType(rows: any, audienceRefType: string) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row: any) => ({ ...row, audienceRefType }));
}

export function useCustomSegmentData({
  isEdit,
  advertiserId,
}: {
  isEdit: boolean;
  advertiserId?: number
}) {
  const isEnabled = Boolean(advertiserId)
  const accountAudience = useBeeswaxAccountAudienceList({
    enabled: isEnabled,
    queryParams: {
      advertiserId,
      status: JSON.stringify(["completed", "processing"]),
    },
  });
  const emailAudience =
    useEmailAudience({
      enabled: isEnabled,
      queryParams: {
        advertiserId,
        status: JSON.stringify(["completed", "processing"]),
      },
    });
  const siteVisiterAudience = useSiteVistorAudienceList({
    enabled: isEnabled,
    advertiserId,
    status: JSON.stringify(["completed", "processing"]),
  });
  const visitorInsightAudience = useTargetableVisitorInsightAudienceList({
    enabled: isEnabled,
    advertiserId,
    status: JSON.stringify(["completed", "processing"]),
  });

  // "Site Visitor" is one list built from two endpoints: general retargeting
  // (BeeswaxRetargetAudiences, a flat array) and Visitor Insight
  // (BeeswaxAudiences with a source, as { count, rows }).
  const siteVisitorData = useMemo(
    () => [
      ...withRefType(
        siteVisiterAudience.data,
        AUDIENCE_REF_TYPES.RetargetAudience
      ),
      // A Visitor Insight audience whose segment hasn't been created yet would be
      // selectable but target nothing, so it stays out of the list until it has one.
      ...withRefType(
        visitorInsightAudience.data?.rows?.filter(
          (row) => row?.BeeswaxSegments?.length
        ),
        AUDIENCE_REF_TYPES.VisitorInsightAudience
      ),
    ],
    [siteVisiterAudience.data, visitorInsightAudience.data]
  );

  const mergedSiteVisitorAudience = {
    ...siteVisiterAudience,
    data: siteVisitorData,
    isLoading: siteVisiterAudience.isLoading || visitorInsightAudience.isLoading,
    isFetching:
      siteVisiterAudience.isFetching || visitorInsightAudience.isFetching,
    // A merged list is only as healthy as both halves: a failed Visitor Insight
    // query must not present a retarget-only list as complete — editing against it
    // would silently rebuild the targeting without the VI segments.
    isError: siteVisiterAudience.isError || visitorInsightAudience.isError,
    error: siteVisiterAudience.error ?? visitorInsightAudience.error,
  };

  // Memoized on the raw lists: these clone every row, and the form re-renders on
  // each keystroke.
  const accountData = useMemo(
    () => withRefType(accountAudience.data, AUDIENCE_REF_TYPES.Audience),
    [accountAudience.data]
  );
  const emailData = useMemo(
    () => withRefType(emailAudience.data, AUDIENCE_REF_TYPES.EmailAudiences),
    [emailAudience.data]
  );
  const accountAudienceWithRefType = {
    ...accountAudience,
    data: accountData,
  };
  const emailAudienceWithRefType = {
    ...emailAudience,
    data: emailData,
  };

  return {
    audiences: {
      [AUDIENCE_TYPES.Audience]: accountAudienceWithRefType,
      [AUDIENCE_TYPES.EmailAudiences]: emailAudienceWithRefType,
      [AUDIENCE_TYPES.RetargetAudience]: mergedSiteVisitorAudience
    }
  };
}
